import type { Map as MaplibreMap } from "maplibre-gl";

// A hidden power-user overlay: pressing the backtick (`) key toggles
// MapLibre's tile-boundary debug view on every live map at once. It outlines
// each tile with its z/x/y label and flags overscaled ("overzoomed") tiles,
// so you can see where a source's real data ends and tiles get stretched past
// their max zoom. Not surfaced in the UI, so it's inert for normal users and
// there for those who know the shortcut.

const liveMaps = new Set<MaplibreMap>();
let boundariesOn = false;
let listenerAttached = false;

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return (
    el?.tagName === "INPUT" ||
    el?.tagName === "TEXTAREA" ||
    !!el?.isContentEditable
  );
}

function handleKey(e: KeyboardEvent): void {
  if (e.key !== "`") return;
  // Don't hijack the key while the user is typing (e.g. a search box).
  if (isTypingTarget(e.target)) return;
  boundariesOn = !boundariesOn;
  for (const m of liveMaps) m.showTileBoundaries = boundariesOn;
  // The overlay lives on the WebGL canvas, so mirror the mode onto <html> as
  // a DOM-visible signal — useful when debugging and the only stable thing an
  // e2e test can assert against.
  if (boundariesOn) {
    document.documentElement.setAttribute("data-tile-boundaries", "on");
  } else {
    document.documentElement.removeAttribute("data-tile-boundaries");
  }
}

/**
 * Wire a map into the shared backtick tile-boundary toggle and return a
 * cleanup function to call when the map is removed. A single global key
 * listener drives every registered map, and a newly registered map adopts
 * the current on/off state so the overlay stays consistent across all maps.
 */
export function registerTileBoundaryToggle(map: MaplibreMap): () => void {
  liveMaps.add(map);
  map.showTileBoundaries = boundariesOn;
  if (!listenerAttached) {
    window.addEventListener("keydown", handleKey);
    listenerAttached = true;
  }
  return () => {
    liveMaps.delete(map);
    if (liveMaps.size === 0 && listenerAttached) {
      window.removeEventListener("keydown", handleKey);
      listenerAttached = false;
    }
  };
}
